import { cn } from '@/lib/utils'

type SliderNavSize = 'sm' | 'md'

/** Shared prev/next control — white circle + green chevron (QBC site style) */
export function sliderNavButtonClass(size: SliderNavSize = 'md', persistent = false) {
  return cn(
    'flex items-center justify-center rounded-full bg-white',
    'shadow-[0_4px_16px_rgba(15,23,42,0.14)] ring-1 ring-neutral-200/80',
    'transition-all duration-200',
    persistent
      ? 'enabled:hover:border-primary-200 enabled:hover:bg-primary-50/40 enabled:hover:shadow-[0_6px_18px_rgba(15,93,154,0.14)] disabled:cursor-default disabled:bg-neutral-100/90 disabled:opacity-100 disabled:shadow-none disabled:ring-neutral-200/50'
      : 'hover:opacity-95 disabled:pointer-events-none disabled:opacity-0',
    size === 'sm' ? 'h-9 w-9' : 'h-10 w-10 sm:h-11 sm:w-11',
  )
}

/** Toolbar pair above split sliders — always visible, dull when disabled */
export function sliderToolbarNavButtonClass(size: SliderNavSize = 'sm') {
  return cn(
    'flex items-center justify-center rounded-full transition-all duration-200',
    'enabled:text-success-600 enabled:hover:bg-white enabled:hover:shadow-sm',
    'disabled:cursor-default disabled:text-neutral-300',
    size === 'sm' ? 'h-8 w-8' : 'h-9 w-9',
  )
}

export function sliderToolbarNavGroupClass() {
  return cn(
    'inline-flex items-center gap-0.5 rounded-full border border-neutral-200/90',
    'bg-linear-to-b from-white to-neutral-50 p-0.5 shadow-[0_2px_10px_rgba(15,23,42,0.06)]',
  )
}

export function sliderNavIconClass(size: SliderNavSize = 'md', dull = false) {
  return cn(
    dull ? 'text-neutral-300' : 'text-success-600',
    size === 'sm' ? 'h-4 w-4' : 'h-5 w-5 sm:h-6 sm:w-6',
  )
}

export const SLIDER_NAV_ICON_STROKE = 2.5

/** Outer wrapper — room for half the button outside the image (matches legacy owl -20px offset) */
export const SLIDER_EDGE_OUTER = 'overflow-visible px-[22px] sm:px-[22px]'

/** Inner wrapper — exact image/track width; buttons anchor to these edges */
export const SLIDER_EDGE_INNER = 'relative'

type SliderNavVertical = 'center' | 'image'

/** Center button on left edge — half inside, half outside (legacy qbcon.in style) */
export function sliderNavPrevClass(size: 'sm' | 'md' = 'md', vertical: SliderNavVertical = 'center') {
  return cn(
    sliderNavButtonClass(size),
    'absolute left-0 z-20 -translate-x-1/2 -translate-y-1/2',
    vertical === 'image' ? 'top-[40%]' : 'top-1/2',
  )
}

export function sliderNavNextClass(size: 'sm' | 'md' = 'md', vertical: SliderNavVertical = 'center') {
  return cn(
    sliderNavButtonClass(size),
    'absolute right-0 z-20 translate-x-1/2 -translate-y-1/2',
    vertical === 'image' ? 'top-[40%]' : 'top-1/2',
  )
}
